import moment from 'moment';

import { ROUTES } from 'module/navigation/enums/routes.enums';

export const SCHEDULE_WEEK_QUERY_PARAM = 'week';
export const SCHEDULE_SELECTED_WEEK_STORAGE_KEY = 'schedule-selected-week';
export const DEFAULT_START_OF_WEEK = 'Monday';

const DAY_MAP: Record<string, number> = {
	Sunday: 0,
	Monday: 1,
	Tuesday: 2,
	Wednesday: 3,
	Thursday: 4,
	Friday: 5,
	Saturday: 6,
};

export type ScheduleDayEntry = {
	'@id'?: string;
	id?: number | string;
	label?: string;
};

export const getDayOfWeekIndex = (startOfWeek = DEFAULT_START_OF_WEEK): number =>
	DAY_MAP[startOfWeek] ?? DAY_MAP[DEFAULT_START_OF_WEEK];

/**
 * Resolve a day label from a weekday name or `/api/days/{id}` IRI.
 */
export const resolveDayLabel = (
	rawValue: string | undefined | null,
	daysData?: ScheduleDayEntry[] | null
): string | null => {
	if (!rawValue) return null;

	if (DAY_MAP[rawValue] !== undefined) {
		return rawValue;
	}

	if (rawValue.startsWith('/api/days/') && daysData?.length) {
		const dayObj = daysData.find((day) => {
			const dayId = day['@id'] || `/api/days/${day.id}`;
			return dayId === rawValue;
		});
		if (dayObj?.label && DAY_MAP[dayObj.label] !== undefined) {
			return dayObj.label;
		}
	}

	return null;
};

/**
 * Resolve org business-week start day label.
 * Prefers `start_of_week`, then `first_day_of_the_week`, then Monday.
 */
export const resolveStartOfWeekLabel = (
	org?: {
		start_of_week?: string;
		first_day_of_the_week?: string;
	} | null,
	daysData?: ScheduleDayEntry[] | null
): string => {
	const fromStartOfWeek = resolveDayLabel(org?.start_of_week, daysData);
	if (fromStartOfWeek) return fromStartOfWeek;

	const fromFirstDay = resolveDayLabel(org?.first_day_of_the_week, daysData);
	if (fromFirstDay) return fromFirstDay;

	return DEFAULT_START_OF_WEEK;
};

/**
 * True when start-of-week can be resolved without racing a Monday default
 * against a pending `/api/days` lookup.
 */
export const isStartOfWeekReady = (
	organizationLoaded: boolean,
	rawStartOfWeek?: string | null,
	daysData?: ScheduleDayEntry[] | null
): boolean => {
	if (!organizationLoaded) return false;
	if (!rawStartOfWeek) return true;
	if (DAY_MAP[rawStartOfWeek] !== undefined) return true;
	if (rawStartOfWeek.startsWith('/api/days/')) {
		return Array.isArray(daysData);
	}
	return true;
};

export const getBusinessWeekStartForDate = (
	date: moment.MomentInput,
	startOfWeek = DEFAULT_START_OF_WEEK
): string => {
	const startDayOfWeek = getDayOfWeekIndex(startOfWeek);
	const currentDate = moment(date);
	const currentDayOfWeek = currentDate.day();

	let daysBack = currentDayOfWeek - startDayOfWeek;
	if (daysBack < 0) {
		daysBack += 7;
	}

	return currentDate.clone().subtract(daysBack, 'days').format('YYYY-MM-DD');
};

export const getCurrentBusinessWeekStart = (startOfWeek = DEFAULT_START_OF_WEEK): string =>
	getBusinessWeekStartForDate(moment(), startOfWeek);

/**
 * Resolve the selected schedule week.
 * URL and localStorage dates are re-snapped to the org start-of-week so a
 * stale Monday-anchored cache cannot drift from a changed business week.
 * Persistence survives module switches; cleared on logout.
 */
export const resolveScheduleWeekStart = (
	search: string,
	startOfWeek = DEFAULT_START_OF_WEEK
): string => {
	const weekFromUrl = new URLSearchParams(search).get(SCHEDULE_WEEK_QUERY_PARAM);
	if (weekFromUrl && moment(weekFromUrl, 'YYYY-MM-DD', true).isValid()) {
		return getBusinessWeekStartForDate(weekFromUrl, startOfWeek);
	}

	const storedWeek = localStorage.getItem(SCHEDULE_SELECTED_WEEK_STORAGE_KEY);
	if (storedWeek && moment(storedWeek, 'YYYY-MM-DD', true).isValid()) {
		return getBusinessWeekStartForDate(storedWeek, startOfWeek);
	}

	return getCurrentBusinessWeekStart(startOfWeek);
};

export const persistScheduleWeekStart = (weekStart: string): void => {
	if (moment(weekStart, 'YYYY-MM-DD', true).isValid()) {
		localStorage.setItem(SCHEDULE_SELECTED_WEEK_STORAGE_KEY, weekStart);
	}
};

export const clearScheduleSelectedWeek = (): void => {
	localStorage.removeItem(SCHEDULE_SELECTED_WEEK_STORAGE_KEY);
};

/**
 * Re-align a persisted selected week to a new org start-of-week.
 * If nothing is stored, no-op (schedule will resolve current week on next open).
 */
export const resnapPersistedScheduleWeek = (startOfWeek: string): string | null => {
	const storedWeek = localStorage.getItem(SCHEDULE_SELECTED_WEEK_STORAGE_KEY);
	if (!storedWeek || !moment(storedWeek, 'YYYY-MM-DD', true).isValid()) {
		return null;
	}
	const snapped = getBusinessWeekStartForDate(storedWeek, startOfWeek);
	persistScheduleWeekStart(snapped);
	return snapped;
};

export const getScheduleReturnPath = (weekStart: string): string =>
	`${ROUTES.HYPERSCHEDULER}?${SCHEDULE_WEEK_QUERY_PARAM}=${weekStart}`;

export type EventNavigationState = {
	event?: unknown;
	eventId?: number | string;
	returnTo?: string;
};

export const getEventDetailLocationFromSchedule = (
	event: { id: number | string },
	scheduleWeekStart: string
): { pathname: string; state: EventNavigationState } => ({
	pathname: ROUTES.EVENT_DETAILS,
	state: {
		event,
		eventId: event.id,
		returnTo: getScheduleReturnPath(scheduleWeekStart),
	},
});

export const resolveEventReturnPath = (
	state: unknown,
	fallback: string = ROUTES.EVENTS
): string => {
	if (state && typeof state === 'object' && 'returnTo' in state) {
		const returnTo = (state as EventNavigationState).returnTo;
		if (typeof returnTo === 'string' && returnTo.trim() !== '') {
			return returnTo;
		}
	}
	return fallback;
};
